import * as React from 'react'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import { Stack, Typography } from '@mui/material'
import { stableSort, getComparator, Order, HeadCell } from '../../utils/tableFunctions'
import EnhancedTableHead from '../table/EnhancedTableHead'
import TableButtonDelete from '../table/TableButtonDelete'
import TableButtonOpen from '../table/TableButtonOpen'
import { RouteConstants } from '../../navigation/navigation-types'
import { InvoiceBriefDto } from '../../services/api/sharedTypes/Invoice'
import { PaymentState } from '../../services/api/sharedTypes/Common'
import TableButtonShow from '../table/TableButtonShow'
import PaymentStateTag from '../common/PaymentStateTag'
import CustomTableCell from '../common/CustomTableCell'
import { Link } from 'react-router-dom'
import { getNumberRoundedInFormattedString } from '../../utils/global-utils'

const headCells: readonly HeadCell[] = [
    {
        id: 'invoiceNumber',
        numeric: true,
        disablePadding: false,
        label: 'Č. faktury',
    },
    {
        id: 'eventName',
        numeric: false,
        disablePadding: false,
        label: 'Událost',
    },
    {
        id: 'issueDate',
        numeric: false,
        disablePadding: false,
        label: 'Vystavena',
    },
    {
        id: 'dueDate',
        numeric: false,
        disablePadding: false,
        label: 'Datum splatnosti',
    },
    {
        id: 'issuerName',
        numeric: false,
        disablePadding: false,
        label: 'Vystaveno od',
    },
    {
        id: 'clientName',
        numeric: false,
        disablePadding: false,
        label: 'Vystaveno na',
    },
    {
        id: 'paymentState',
        numeric: false,
        disablePadding: false,
        label: 'Zaplaceno',
        centered: true,
    },
    {
        id: 'invoicedWithoutTax',
        numeric: false,
        disablePadding: false,
        label: 'Částka',
        align: 'right',
    },
    {
        id: 'buttons',
        numeric: false,
        disablePadding: false,
        label: '',
    },
]

interface InvoiceTableProps {
    invoices: InvoiceBriefDto[]
    onDelete: (id: string) => void
}

const InvoiceTable: React.FC<InvoiceTableProps> = ({ invoices, onDelete }: InvoiceTableProps) => {
    const [order, setOrder] = React.useState<Order>('desc')
    const [orderBy, setOrderBy] = React.useState<string>('issueDate')
    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(50)

    const theme = useTheme()

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    return (
        <>
            <Typography variant='body2' style={{ marginBottom: theme.spacing(3) }}>
                {invoices.length} záznamů
            </Typography>
            <Box style={{ width: '100%' }}>
                <TableContainer>
                    <Table>
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            headCells={headCells}
                            rowCount={invoices.length}
                        />
                        <TableBody>
                            {stableSort(
                                invoices.map((row) => {
                                    return {
                                        ...row,
                                        issueDate: new Date(row.issueDate).getTime(),
                                        dueDate: new Date(row.dueDate).getTime(),
                                        eventIdName: row.eventIdName.name,
                                        eventName: row.eventIdName ? row.eventIdName.name : '',
                                        eventId: row.eventIdName.id ? row.eventIdName.id : '',
                                        createdAt: new Date(row.createdAt).toLocaleDateString(),
                                        lastModifiedAt: new Date(row.lastModifiedAt).toLocaleDateString(),
                                    }
                                }),
                                getComparator(order, orderBy)
                            )
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, index: number) => {
                                    return (
                                        <TableRow
                                            hover
                                            tabIndex={-1}
                                            key={index}
                                            style={{
                                                backgroundColor:
                                                    row.paymentState !== PaymentState.NotSet
                                                        ? row.paymentState === PaymentState.Paid ||
                                                            row.paymentState === PaymentState.PaidWithoutInvoice ||
                                                            row.paymentState === PaymentState.Free
                                                            ? theme.palette.tableRow?.background.paid
                                                            : theme.palette.tableRow?.background.notPaid
                                                        : '',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <CustomTableCell isText route={RouteConstants.events + '/' + row.eventId}>
                                                {row.invoiceNumber}
                                            </CustomTableCell>
                                            <CustomTableCell style={{
                                                maxWidth: 450,
                                            }} isText route={RouteConstants.events + '/' + row.eventId}>
                                                {row.eventName}
                                            </CustomTableCell>
                                            <CustomTableCell isText route={RouteConstants.events + '/' + row.eventId}>
                                                {new Date(row.issueDate).toLocaleDateString('cs')}
                                            </CustomTableCell>
                                            <CustomTableCell isText route={RouteConstants.events + '/' + row.eventId}>
                                                {new Date(row.dueDate).toLocaleDateString('cs')}
                                            </CustomTableCell>
                                            <CustomTableCell isText route={RouteConstants.events + '/' + row.eventId}>
                                                {row.issuerName}
                                            </CustomTableCell>
                                            <CustomTableCell isText route={RouteConstants.events + '/' + row.eventId}>
                                                {row.clientName}
                                            </CustomTableCell>
                                            <CustomTableCell route={RouteConstants.events + '/' + row.eventId}>
                                                <PaymentStateTag paymentState={row.paymentState} />
                                            </CustomTableCell>
                                            <CustomTableCell route={RouteConstants.events + '/' + row.eventId} align='right'>
                                                {getNumberRoundedInFormattedString(row.invoicedWithoutTax)}
                                            </CustomTableCell>
                                            <CustomTableCell>
                                                <Stack spacing={theme.spacing(2)} direction='row'>
                                                    <Link to={RouteConstants.invoices + '/' + row.id}>
                                                        <TableButtonShow />
                                                    </Link>
                                                    <TableButtonOpen route={RouteConstants.events} id={row.eventId} />
                                                    <TableButtonDelete
                                                        nameOfEntityInDialogMessage={'Fakturu č. ' + row.invoiceNumber.toString()}
                                                        onClick={() => onDelete(row.id)}
                                                    />
                                                </Stack>
                                            </CustomTableCell>
                                        </TableRow>
                                    )
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[50, 100, 150]}
                    component='div'
                    count={invoices.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Box>
        </>
    )
}

export default InvoiceTable
